import { IDirective, IDirectiveFactory, IFilterService, IScope } from "angular";
import dayjs from "@gtmhub/dayjs";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { localize } from "@gtmhub/localization";
import { Chartist, ILineChartOptions } from "@gtmhub/shared/libs/chartist/chartist.module";
import { IMetricSnapshot, Metric } from "@webapp/okrs/metrics/models/metric.models";
import { calculateDelta, setNextMilestone } from "@webapp/okrs/metrics/utils/milestone.util";
import { MetricValueFilter } from "../filters/metric-value.filter";
import { formatAttainmentProgressValue } from "../utils";

interface TrendChartScope extends IScope {
  goal: IGoal;
  metric: Metric;
  id: string;
}

export class TrendChart implements IDirective {
  scope = {
    metric: "=",
    goal: "=",
    id: "@",
  };
  restrict = "E";
  transclude = true;
  template = "<div id='metric-trend-{{id}}' class='metric-trend-chart'></div>";

  constructor(private goalService: GoalService, private $filter: IFilterService) {}
  link(scope: TrendChartScope): void {
    const dateFormat = {
      ddmmm: "DD MMM",
      ddmmmyy: "DD MMM YY",
      ddmmmyyyy: "DD MMM YYYY",
    };

    const data = {
      labels: [],
      series: [],
    };

    const projectionIndex = 0;
    const noGapsIndex = 1;
    const actualIndex = 2;
    const hiddenIndex = 3;

    data.series[projectionIndex] = {
      name: "projection",
      data: [],
    };

    data.series[noGapsIndex] = {
      name: "nogaps",
      data: [],
    };

    data.series[actualIndex] = {
      name: "actual",
      data: [],
    };

    // hidden line is added to have values at indexes of deadlines so they can be drawn at the right index
    data.series[hiddenIndex] = {
      name: "hidden",
      data: [],
      className: "hidden",
    };

    // object to identify the correct date/index where to place the check-in value
    const dateDataIndexMap: { [date: string]: number } = {};

    // +1 added to difference to include last day of the period
    const duration: number = dayjs(scope.goal.dateTo).diff(scope.goal.dateFrom, "days") + 1;

    let hardDeadline: string,
      softDeadline: string,
      hardDeadlinePosition: string,
      softDeadlinePosition: string,
      hardDeadlinePositionLabel: string,
      softDeadlinePositionLabel: string;
    // if no deadlines plugin will not draw due date lines cause of 'null'
    let hardDeadlineObject: { position: string; className: string; label: string; positionLabel: string } = null;
    let softDeadlineObject: { position: string; className: string; label: string; positionLabel: string } = null;

    const processLabelsAndItsMap = (): void => {
      // setting labels and date-index map
      for (let i = 0; i < duration; i++) {
        const dateToAdd = dayjs(scope.goal.dateFrom).add(i, "days").format(dateFormat.ddmmmyyyy);
        const dateToAddLabel = dayjs(scope.goal.dateFrom).startOf("day").add(i, "days").format();

        dateDataIndexMap[dateToAdd] = i;

        data.labels.push(dateToAddLabel);
      }
    };

    /**
     * Gets the last KR update made before the beginning of the current session
     * @return the last update or undefined
     */
    const getLastUpdateBeforeSessionStart = (): IMetricSnapshot => {
      const lastKRUpdateBeforeClone = scope.metric.metricHistory.filter((u) => u.createdAt < scope.goal.dateFrom);

      return lastKRUpdateBeforeClone[lastKRUpdateBeforeClone.length - 1];
    };

    const processDeadlinesAndProjected = (dateFormatString: string): void => {
      // formatting soft and hard deadlines in format 01 Jan 2001
      hardDeadline = dayjs(scope.metric.dueDate).format(dateFormat.ddmmmyyyy);
      softDeadline = dayjs(scope.metric.softDueDate).format(dateFormat.ddmmmyyyy);

      // used to determine the x-position of the vertical line
      hardDeadlinePosition = dayjs(scope.metric.dueDate).hour(0).minute(0).second(0).format();
      softDeadlinePosition = dayjs(scope.metric.softDueDate).hour(0).minute(0).second(0).format();

      // used to display the soft and hard deadlines' labels
      hardDeadlinePositionLabel = dayjs(scope.metric.dueDate).format(dateFormatString);
      softDeadlinePositionLabel = dayjs(scope.metric.softDueDate).format(dateFormatString);

      // setting first point of projection dashed line to be at the start of chart with metric initial value
      // or if the metric is cloned, with latest metric update value before the session start
      // second point is set inside processDeadline method depending on if there are deadlines
      const latestKRUpdate: IMetricSnapshot = getLastUpdateBeforeSessionStart();
      if (latestKRUpdate) {
        data.series[projectionIndex].data.push(latestKRUpdate.metricValue);
      } else {
        data.series[projectionIndex].data.push(scope.metric.initialValue);
      }

      // when no deadlines are set date is something like 01 01 `0`001
      // in `` is the symbol checked
      if (hardDeadline.split(" ")[2][0] === "2") {
        hardDeadlineObject = {
          position: hardDeadlinePosition,
          className: "hard-deadline",
          label: localize("hard_deadline"),
          positionLabel: hardDeadlinePositionLabel,
        };

        const indexOfHard: number = dateDataIndexMap[hardDeadline];
        // setting the end of projection line to be on the hard due date with value = metric target
        data.series[projectionIndex].data[indexOfHard] = scope.metric.target;

        if (softDeadline.split(" ")[2][0] === "2") {
          softDeadlineObject = {
            position: softDeadlinePosition,
            className: "soft-deadline",
            label: localize("soft_deadline"),
            positionLabel: softDeadlinePositionLabel,
          };

          const indexOfSoft: number = dateDataIndexMap[softDeadline];
          // if there is a deadline and no value is found at its index it will be placed outside chart
          // adding points of the hidden line to display the deadlines at the right place
          data.series[hiddenIndex].data[indexOfHard] = scope.metric.initialValue;
          data.series[hiddenIndex].data[indexOfSoft] = scope.metric.initialValue;
        }
      } else {
        // set the projection line to end at the end of session period (goal date to in this case)
        data.series[projectionIndex].data[duration - 1] = scope.metric.target;
      }
      scope.metric.milestones?.forEach((ms) => {
        const dayNumber = dayjs(ms.date).diff(scope.goal.dateFrom, "days");
        data.series[projectionIndex].data[dayNumber] = ms.targetValue;
      });
    };

    const processProgressLine = (): void => {
      // metric progress("nogaps") line will start from the bottom/top left corner where start date and initial value cross

      const latestKRUpdate: IMetricSnapshot = getLastUpdateBeforeSessionStart();
      if (latestKRUpdate) {
        data.series[noGapsIndex].data.push({ meta: { attainmentChange: null }, value: latestKRUpdate.metricValue });
      } else {
        data.series[noGapsIndex].data.push({ meta: { attainmentChange: null }, value: scope.metric.initialValue });
      }

      // setting values of updates --> progress("nogaps")
      for (const record of scope.metric.metricHistory) {
        const dateOfUpdate: string = dayjs(record.updatedAt).format(dateFormat.ddmmmyyyy);

        // getting index from the map to gain constant access to array entry
        const index: number = dateDataIndexMap[dateOfUpdate];
        // and assigning it
        data.series[noGapsIndex].data[index] = { meta: { attainmentChange: record.attainmentChange }, value: record.metricValue };
        setNextMilestone(data.series[noGapsIndex].data[index].meta, scope.metric, record.updatedAt);
      }
    };

    const processActualPoint = (): void => {
      // latest update snapshot of the metric
      const snapshotCount: number = scope.metric.metricHistory.length;

      // if there is an update in metric history
      if (snapshotCount > 0) {
        // use the last update to draw the point("actual")
        const latestSnapshot: IMetricSnapshot = scope.metric.metricHistory[snapshotCount - 1];
        const dateOfUpdate: string = dayjs(latestSnapshot.updatedAt).format(dateFormat.ddmmmyyyy);
        const index: number = dateDataIndexMap[dateOfUpdate];

        data.series[actualIndex].data[index] = { meta: { attainmentChange: latestSnapshot.attainmentChange }, value: latestSnapshot.metricValue };
        setNextMilestone(data.series[actualIndex].data[index].meta, scope.metric, latestSnapshot.updatedAt);
      } else {
        // use the metric initial value to draw the point("actual")
        data.series[actualIndex].data.push({ meta: { attainmentChange: null }, value: scope.metric.initialValue });
        setNextMilestone(data.series[actualIndex].data[0].meta, scope.metric, scope.goal.dateFrom);
      }
    };

    const addThousandsSeparator = (value: number): string => {
      return value.toLocaleString("en");
    };

    const setTooltip = (meta: string, value: number) => {
      const metaParsed = JSON.parse(meta.replace(/&quot;/g, '"')).data;
      const attainmentChange = (metaParsed || {}).attainmentChange;
      const formattedMetricValue = this.$filter<MetricValueFilter>("metricValue")(value, scope.metric.format);

      if (metaParsed.nextMilestone) {
        const delta = calculateDelta(metaParsed.nextMilestone.targetValue, value);
        const badgeClass = delta < 0 ? "danger" : delta > 0 ? "locked" : "";
        const formattedAttainmentChange = "<span class='gh-badge " + badgeClass + " chartist-tooltip-meta'>" + delta + "</span>";
        return formattedMetricValue + formattedAttainmentChange;
      } else if (attainmentChange || attainmentChange === 0) {
        const badgeClass = attainmentChange < 0 ? "danger" : attainmentChange > 0 ? "locked" : "";
        const badgeSign = attainmentChange > 0 ? "+" : "";
        const formattedAttainmentChange =
          "<span class='gh-badge " + badgeClass + " chartist-tooltip-meta'>" + badgeSign + formatAttainmentProgressValue(attainmentChange) + "%</span>";
        return formattedMetricValue + formattedAttainmentChange;
      }
      return formattedMetricValue;
    };

    const calculateMaxActualLength = (metricHistory: IMetricSnapshot[]): number => {
      if (!Array.isArray(metricHistory)) {
        return 0;
      }

      return metricHistory.reduce((maxActualLength: number, currentSnapshot: IMetricSnapshot): number => {
        const currentSnapshotActualLength: number = addThousandsSeparator(currentSnapshot.metricValue).length;

        return currentSnapshotActualLength > maxActualLength ? currentSnapshotActualLength : maxActualLength;
      }, 0);
    };

    const configureChartParamsAndDraw = (): void => {
      // calculating year difference of session's start and end
      const yearDifference: number = dayjs(scope.goal.dateTo).diff(scope.goal.dateFrom, "years");
      // setting date format to display year if session starts and ends in different years
      const dateFormatString = yearDifference > 0 ? dateFormat.ddmmmyy : dateFormat.ddmmm;

      // interval of labels placed on x axis
      const interval: number = Math.round(duration / 4);

      // calculating biggest number on Y axis length and offset needed to display any value properly
      const maxActualValueLength: number = calculateMaxActualLength(scope.metric.metricHistory);
      const maxValueLength: number = Math.max(
        addThousandsSeparator(scope.metric.target).length,
        addThousandsSeparator(scope.metric.initialValue).length,
        maxActualValueLength
      );
      const [safetyPaddingYPx, digitSizePx] = [5, 5];

      processLabelsAndItsMap();

      processDeadlinesAndProjected(dateFormatString);

      processProgressLine();

      processActualPoint();

      const chartWidth = 460;
      const chartPadding = {
        top: 15,
        right: 50,
        bottom: 5,
        left: 40,
      };

      const options: ILineChartOptions = {
        width: `${chartWidth}px`,
        chartPadding: chartPadding,
        series: {
          nogaps: {
            lineSmooth: Chartist.Interpolation.simple({
              fillHoles: true,
            }),
            showArea: true,
          },
        },
        axisX: {
          labelInterpolationFnc: function skipLabels(value, index) {
            // include last date goal.dateTo in x axis
            if (index + 1 === duration) {
              const date = new Date(value);
              return dayjs(date).format(dateFormatString);
            }

            if (index % interval === 0) {
              const date = new Date(value);
              return dayjs(date).format(dateFormatString);
            }

            return null;
          },
        },
        axisY: {
          // the offset of the labels to the chart area
          offset: safetyPaddingYPx + maxValueLength * digitSizePx,
          // position where labels are placed. Can be set to `start` or `end` where `start` is equivalent to left or top on vertical axis
          // and `end` is equivalent to right or bottom on horizontal axis.
          position: "start",
          labelInterpolationFnc: addThousandsSeparator,
        },
        plugins: [
          Chartist.plugins.verticalLine(hardDeadlineObject),
          Chartist.plugins.verticalLine(softDeadlineObject),
          Chartist.plugins.tooltip({
            tooltipOffset: (width: number) => {
              return { x: width / 2, y: 0 };
            },
            tooltipFnc: (meta: string, value: number) => {
              return setTooltip(meta, value);
            },
          }),
        ],
      };

      const containerId = `metric-trend-${scope.id}`;
      window.setTimeoutOutsideAngular(() => {
        const container = document.getElementById(containerId);
        if (container) {
          new Chartist.Line(container, data, options);
        }
      }, 100);
    };

    if (!scope.goal) {
      const goalFields = "dateFrom,dateTo,id,name,ownerIds,sessionId";

      this.goalService.getGoalWithFields(scope.metric.goalId, goalFields).then(
        (goal) => {
          scope.goal = goal;

          configureChartParamsAndDraw();
        },
        (error) => {
          console.error(error);
        }
      );
    } else {
      configureChartParamsAndDraw();
    }
  }

  static factory(): IDirectiveFactory {
    const directive = (goalService: GoalService, $filter: IFilterService) => new TrendChart(goalService, $filter);
    directive.$inject = ["GoalService", "$filter"];
    return directive;
  }
}
